/**
 * ActionRequiredPanel — Unified "what to do next" section for execution surfaces.
 *
 * Merges blockers + next-steps + suggested actions into a single, action-first
 * panel. Each row has:
 *   - state (severity dot + label)
 *   - short explanation
 *   - one primary CTA (verb button, executed via runAction)
 *   - optional secondary "Open" link
 *
 * Used by: QuoteDetail, OrderingQueue, (future) CustomerProfile.
 * Job Detail keeps its bespoke layout per the action-first redesign.
 *
 * NON-DESTRUCTIVE: This is purely additive — consumers opt in.
 */
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { AlertTriangle, AlertCircle, Info, CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import { runAction, type ExecutableAction } from "@/lib/actionRunner";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export type ActionSeverity = "hard" | "warning" | "info" | "suggest";

export interface ActionRequiredItem {
  id: string;
  /** Severity drives the dot color and ordering. */
  severity: ActionSeverity;
  /** Short headline (e.g. "Contract Not Signed"). */
  label: string;
  /** One-line explanation of why this needs attention. */
  detail?: string;
  /** Primary CTA — verb button. Optional: if omitted, only the secondary link shows. */
  primary?: ExecutableAction & { label: string; disabled?: boolean; disabledTitle?: string };
  /** Secondary link — usually "Open" to deep-dive. Optional. */
  secondaryHref?: string;
  secondaryLabel?: string;
}

interface ActionRequiredPanelProps {
  title?: string;
  items: ActionRequiredItem[];
  /** Shown when items is empty. Defaults to a clean checkmark state. */
  emptyState?: { label: string; detail?: string };
  /** Limit how many rows render. Defaults to all. */
  maxItems?: number;
  className?: string;
}

const SEVERITY_STYLES: Record<ActionSeverity, { dot: string; icon: React.ElementType; label: string }> = {
  hard: { dot: "bg-destructive", icon: AlertTriangle, label: "Blocker" },
  warning: { dot: "bg-warning/60", icon: AlertCircle, label: "At Risk" },
  info: { dot: "bg-info/60", icon: Info, label: "Heads up" },
  suggest: { dot: "bg-primary", icon: Sparkles, label: "Suggested" },
};

const SEVERITY_ORDER: Record<ActionSeverity, number> = {
  hard: 0,
  warning: 1,
  suggest: 2,
  info: 3,
};

export function ActionRequiredPanel({
  title = "Action Required",
  items,
  emptyState,
  maxItems,
  className,
}: ActionRequiredPanelProps) {
  const navigate = useNavigate();

  const sorted = [...items].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
  const visible = maxItems ? sorted.slice(0, maxItems) : sorted;
  const hidden = maxItems ? Math.max(0, sorted.length - maxItems) : 0;

  if (visible.length === 0) {
    return (
      <Card className={cn("border-success/30 bg-success/5", className)}>
        <CardContent className="p-4 flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-success shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">
              {emptyState?.label ?? "All clear"}
            </p>
            {emptyState?.detail && (
              <p className="text-xs text-muted-foreground mt-0.5">{emptyState.detail}</p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  const handlePrimary = async (item: ActionRequiredItem) => {
    if (!item.primary) return;
    const handled = await runAction(item.primary, {
      navigate,
      onError: (err) => {
        console.error("[ActionRequiredPanel] action failed", err);
        toast.error("Couldn't complete that action");
      },
    });
    if (!handled && item.secondaryHref) {
      navigate(item.secondaryHref);
    }
  };

  return (
    <Card className={cn("border-2", className)}>
      <CardContent className="p-0">
        <div className="px-4 py-3 border-b bg-muted/20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-foreground" />
            <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
            <span className="text-xs text-muted-foreground">
              {sorted.length} item{sorted.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
        <div className="divide-y">
          {visible.map((item) => {
            const style = SEVERITY_STYLES[item.severity];
            const Icon = style.icon;
            return (
              <div
                key={item.id}
                className="flex items-start gap-3 px-4 py-3 hover:bg-muted/20 transition-colors"
              >
                <div className="flex items-center gap-2 shrink-0 pt-0.5">
                  <span className={cn("h-2 w-2 rounded-full", style.dot)} aria-hidden />
                  <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-tight">{item.label}</p>
                  {item.detail && (
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                      {item.detail}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {item.primary && (
                    <Button
                      size="sm"
                      variant={item.severity === "hard" ? "default" : "outline"}
                      className="h-8 text-xs gap-1"
                      disabled={item.primary.disabled}
                      title={item.primary.disabledTitle}
                      onClick={() => handlePrimary(item)}
                    >
                      {item.primary.label}
                      <ArrowRight className="h-3 w-3" />
                    </Button>
                  )}
                  {item.secondaryHref && !item.primary && (
                    <Link to={item.secondaryHref}>
                      <Button size="sm" variant="ghost" className="h-8 text-xs gap-1">
                        {item.secondaryLabel ?? "Open"}
                        <ArrowRight className="h-3 w-3" />
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {hidden > 0 && (
          <div className="px-4 py-2 text-xs text-muted-foreground border-t bg-muted/10 text-center">
            +{hidden} more — resolve top items first
          </div>
        )}
      </CardContent>
    </Card>
  );
}
